<template>
	<div
		class="lg:max-w-[280px] lg:w-full lg:relative absolute lg:top-none top-0 w-1/2"
	>
		<button
			class="flex-1 md:flex-none flex lg:hidden items-center"
			@click="filterMenuOpened = true"
		>
			<FunnelIcon class="w-5 h-5 text-gray-500 mr-2" />
			<span class="text-sm text-gray-700 font-medium lg:pb-4">{{
				$t("Filters")
			}}</span>
		</button>
		<div class="lg:mb-8">
			<p class="text-sm font-medium mb-6 lg:block hidden">
				{{ $t("Filters") }}
			</p>
			<div class="gap-2 flex mb-2 flex-wrap">
				<div
					v-for="filter of selectedFiltersToDisplay"
					:key="filter.Id"
					class="flex items-center h-[42px] shadow-sm px-5 bg-graycustom hover:bg-gray-dark cursor-pointer"
					@click="toggleFilter(filter.Id, selectedFilters)"
				>
					<span class="text-primary text-base font-medium">
						{{ filter.Name }}
					</span>
					<XMarkIcon class="ml-3 w-5 h-5 cursor-pointer text-gray-super-dark" />
				</div>
			</div>

			<button
				v-if="selectedFiltersToDisplay?.length"
				class="text-gray-900 py-2 underline font-medium text-base"
				@click="selectedFilters = deselectAllFilters()"
			>
				{{ $t("Clear all") }}
			</button>
		</div>

		<div v-for="blok in bloks" :key="blok._uid" class="hidden lg:block">
			<StoryblokComponent
				v-if="filters?.[blok.filter_value as keyof typeof filters]"
				v-model="selectedFilters"
				:blok="blok"
				:filters="filters[blok.filter_value as keyof typeof filters]"
			/>
		</div>

		<TransitionRoot :show="filterMenuOpened" appear as="template">
			<Dialog as="div" class="lg:hidden" @close="close">
				<TransitionChild
					as="template"
					enter="duration-300 ease-out"
					enter-from="opacity-0"
					enter-to="opacity-100"
					leave="duration-200 ease-in"
					leave-from="opacity-100"
					leave-to="opacity-0"
				>
					<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
				</TransitionChild>
				<TransitionChild
					as="template"
					enter="duration-300 ease-out"
					enter-from="-translate-x-full"
					enter-to="translate-x-0"
					leave="duration-200 ease-in"
					leave-from="translate-x-0"
					leave-to="-translate-x-full"
				>
					<DialogPanel
						class="flex flex-col fixed inset-y-0 left-0 z-50 w-full overflow-y-auto bg-white sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
					>
						<div class="container py-6 flex items-center justify-between px-4">
							<div>
								<h4 class="font-medium text-lg">{{ $t("Filters") }}</h4>
							</div>
							<button
								type="button"
								class="-m-2.5 rounded-md p-2.5 text-gray-700 outline-none"
								@click="close"
							>
								<span class="sr-only">{{ $t("close_menu") }}</span>
								<XMarkIcon class="h-6 w-6" aria-hidden="true" />
							</button>
						</div>
						<div class="flex-1 min-h-0 overflow-y-auto">
							<div v-for="blok in bloks" :key="blok._uid">
								<StoryblokComponent
									v-if="filters?.[blok.filter_value as keyof typeof filters]"
									v-model="selectedFilters"
									:blok="blok"
									:filters="filters[blok.filter_value as keyof typeof filters]"
								/>
							</div>
						</div>
						<div class="container p-4">
							<button
								class="w-full text-white text-base font-medium py-3 px-6 bg-gray-800 shadow-sm disabled:opacity-50"
								@click="close"
							>
								{{ $t("Show Products", [getTotal]) }}
							</button>
						</div>
					</DialogPanel>
				</TransitionChild>
			</Dialog>
		</TransitionRoot>
	</div>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import { FunnelIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import type {
	GenericFilterItem,
	GroupedFilters,
	SelectedFilter
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { BlokCategoryWidget } from "~/types/storyblok/storyblok";

interface Props {
	bloks: BlokCategoryWidget[] | undefined;
	filters: GroupedFilters;
	initialSelectedFilters?: SelectedFilter;
}

const { bloks, filters, initialSelectedFilters } = defineProps<Props>();

const { deselectFilter, deselectAllFilters, printSelectedFilters } =
	useNorcePwa();

const emit = defineEmits(["sendSelectedFilters"]);
const selectedFilters = ref<SelectedFilter>(initialSelectedFilters || {});
const filtersRef = toRef<() => GroupedFilters>(() => filters);
const selectedFiltersToDisplay = ref<GenericFilterItem[]>(
	printSelectedFilters({
		selectedFilters: selectedFilters.value,
		filters
	})
);
const filterMenuOpened = ref<boolean>(false);
const getTotal = ref(0);

const close = () => {
	filterMenuOpened.value = false;
};

const emitToCategory = ({
	selectedFilters,
	filters
}: {
	selectedFilters: SelectedFilter | undefined;
	filters: GroupedFilters;
}) => {
	emit("sendSelectedFilters", { selectedFilters }, { resetPageNo: true });

	if (selectedFilters) {
		selectedFiltersToDisplay.value = printSelectedFilters({
			selectedFilters,
			filters
		});
	}
};

const toggleFilter = (
	selectedFilterId: string,
	filters: SelectedFilter | undefined
) => {
	if (filters) {
		selectedFilters.value = deselectFilter(selectedFilterId, filters);
	}
};

watch(selectedFilters, () => {
	emitToCategory({
		selectedFilters: selectedFilters.value,
		filters: filtersRef?.value
	});
});
</script>
